import React, { useEffect, useMemo, useState } from "react";
import { NoTranslateTextarea } from "../NoTranslateTextarea";
import { ManualTranslationRequestForm } from "../manual-translation-request/ManualTranslationRequestForm";
import {
  Flag,
  LanguageCode,
  renderLanguageFromCode,
  SUICustomerLanguageDropdown,
} from "../../customers/customerlanguages";
import { isEmpty } from "lodash";
import { useSelector } from "react-redux";
import { Dimmer, Icon } from "semantic-ui-react";
import { useGetAuthQuery } from "../../api/authApi";
import { useGetCustomerQuery } from "../../api/customerApi";
import { requestCustomRequest } from "../../api/requestTranslationApi";
import { Spinner } from "../../utils/Spinner";
import { RootState } from "../../utils/store";
import { Text } from "../../components/Text";
import { Button } from "../../components/tailwind";
import { NOOP } from "../..";

export const CustomRequestText: React.FC = () => {
  const { data: customer, isLoading } = useGetCustomerQuery();
  const { data: auth, isLoading: isAuthLoading } = useGetAuthQuery();
  const token = useSelector((state: RootState) => state.auth.token);
  const [isRequesting, setIsRequesting] = useState(false);
  const [jobIds, setJobIds] = useState<{ [lang: string]: string }>({});
  const [email, setEmail] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [sourceText, setSourceText] = useState("");

  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<
    LanguageCode
  >();
  const [selectedLanguages, setSelectedLanguages] = useState<LanguageCode[]>(
    []
  );

  const allLanguagesHasBeenSelected =
    selectedLanguages.length === customer?.languages.length - 1;

  useEffect(() => {
    if (selectedLanguages.includes(selectedSourceLanguage)) {
      setSelectedLanguages(
        [...selectedLanguages].filter((code) => code !== selectedSourceLanguage)
      );
    }
  }, [selectedSourceLanguage]);

  useEffect(() => {
    if (customer && customer.config.tag_input_language) {
      setSelectedSourceLanguage(customer.config.tag_input_language);
    }
  }, [customer]);

  const handleSelectLanguage = (code: LanguageCode): void => {
    if (code === selectedSourceLanguage) {
      return;
    }
    if (selectedLanguages.includes(code)) {
      setSelectedLanguages((prev) => [...prev].filter((c) => c !== code));
      return;
    }
    setSelectedLanguages((prev) => [...prev, code]);
  };

  const handleSelectAll = (): void => {
    if (allLanguagesHasBeenSelected) {
      setSelectedLanguages([]);
      return;
    }
    const languageCodesToSelect = customer.languages
      .filter(({ code }) => code !== selectedSourceLanguage)
      .map(({ code }) => code);
    setSelectedLanguages(languageCodesToSelect);
  };

  const handleRequestCustomRequest = async (): Promise<void> => {
    if (!canSendRequest) return;
    setIsRequesting(true);
    try {
      const results = await Promise.all(
        selectedLanguages.map((language) => {
          return requestCustomRequest(
            token,
            email,
            selectedSourceLanguage,
            [language],
            isUrgent,
            additionalInstructions,
            null,
            sourceText
          ).then((jobId) => {
            return { language, jobId };
          });
        })
      );
      setJobIds((prevJobIds) => {
        const newJobIds = { ...prevJobIds };
        results.forEach(({ language, jobId }) => {
          newJobIds[language] = jobId;
        });
        return newJobIds;
      });
    } finally {
      setIsRequesting(false);
    }
  };

  const resetAllStates = (): void => {
    setJobIds({});
    setIsUrgent(false);
    setAdditionalInstructions("");
    setSelectedSourceLanguage(null);
    setSelectedLanguages([]);
    setIsRequesting(false);
    setSourceText("");
  };

  useEffect(() => {
    if (isAuthLoading || !auth) return;
    setEmail(auth.user.email);
  }, [auth, isAuthLoading]);

  const canSendRequest = useMemo((): boolean => {
    return !!(
      selectedSourceLanguage &&
      email &&
      selectedLanguages.length &&
      sourceText &&
      !!additionalInstructions
    );
  }, [
    selectedSourceLanguage,
    selectedLanguages,
    email,
    sourceText,
    additionalInstructions,
  ]);
  return (
    <>
      <div className="tw-grid tw-grid-cols-2 tw-gap-8">
        <div className="tw-mt-4">
          <SUICustomerLanguageDropdown
            data-testid="custom-request-text-source-dropdown"
            loading={isLoading}
            clearable
            fluid
            placeholder="Select source language"
            selection
            value={selectedSourceLanguage}
            onChange={(e, { value }): void =>
              setSelectedSourceLanguage(value as LanguageCode)
            }
          />
          <div>
            <NoTranslateTextarea
              value={sourceText}
              onChange={setSourceText}
              testId="custom-request-text-textarea"
              placeholder="Enter text to translate..."
            ></NoTranslateTextarea>
            <ManualTranslationRequestForm
              email={email}
              setEmail={setEmail}
              isUrgent={isUrgent}
              setIsUrgent={setIsUrgent}
              additionalInstructions={additionalInstructions}
              setAdditionalInstructions={setAdditionalInstructions}
              additionalInstructionLabel="Explain your issue *"
            />
          </div>
        </div>
        <div>
          <table className="txu-table txu-compact txu-clickable-rows tw-mb-4">
            <thead>
              <tr>
                <th>Language</th>
                <th>Included in Custom Request</th>
              </tr>
            </thead>
            <tbody>
              {customer?.languages.map((language) => (
                <tr
                  data-testid={`custom-request-table-row-${language.code}`}
                  key={language.code}
                  onClick={(): void => handleSelectLanguage(language.code)}
                >
                  <td>
                    <Flag content={language.flag} /> {language.short_name}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      className="txu-input"
                      data-testid={`custom-request-table-row-${language.code}-checkbox`}
                      checked={selectedLanguages.includes(language.code)}
                      disabled={language.code === selectedSourceLanguage}
                      onChange={NOOP}
                    />
                  </td>
                </tr>
              ))}
              <tr
                onClick={(): void => handleSelectAll()}
                data-testid="custom-request-text-select-all"
              >
                <td>Select All</td>
                <td>
                  <input
                    data-testid="custom-request-text-select-all-checkbox"
                    type="checkbox"
                    checked={allLanguagesHasBeenSelected}
                    onChange={(): void => handleSelectAll()}
                    className="txu-input"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <Button
            variant="primary"
            content="Send request"
            className="tw-float-end"
            data-testid="custom-request-text-send-button"
            onClick={(): Promise<void> => handleRequestCustomRequest()}
            disabled={!canSendRequest}
          />
        </div>
      </div>

      <Dimmer active={isRequesting || !isEmpty(jobIds)} page>
        {isRequesting ? (
          <Spinner inverted content="Sending Custom request..." size="medium" />
        ) : (
          <>
            <Icon name="check" />
            <Text size="large">Custom Request received!</Text>
            <Text size="small">
              You will receive an email at <b>{email}</b> when the Custom
              request is complete.
            </Text>
            <ul>
              {Object.entries(jobIds).map(([languageCode, jobId]) => {
                return (
                  <li key={languageCode} className="tw-my-2">
                    <strong>
                      {renderLanguageFromCode(
                        languageCode,
                        customer?.languages ?? []
                      )}
                    </strong>
                    : {jobId}
                  </li>
                );
              })}
            </ul>
            <Text size="small">
              Save the job ID(s) for use in correspondence with us.
              <br />
              Any related questions can be sent to{" "}
              <a
                href="mailto:translations@textual.se"
                style={{ color: "white" }}
              >
                translations@textual.se
              </a>
            </Text>
            <hr className="tw-my-4 tw-border-gray-600" />
            <Button content="Done" variant="primary" onClick={resetAllStates} />
          </>
        )}
      </Dimmer>
    </>
  );
};
